'use client';

import { c as _c } from "react/compiler-runtime";
import { type MotionValue, motion, motionValue, type Transition, useSpring, useTransform } from 'motion/react';
import { useEffect, useId } from 'react';
import useMeasure from 'react-use-measure';
const TRANSITION: Transition = {
  type: 'spring',
  stiffness: 280,
  damping: 18,
  mass: 0.3
};
function Digit(t0) {
  const $ = _c(10);
  const {
    value,
    place
  } = t0;
  const valueRoundedToPlace = Math.floor(value / place) % 10;
  let t1;
  if ($[0] !== valueRoundedToPlace) {
    t1 = motionValue(valueRoundedToPlace);
    $[0] = valueRoundedToPlace;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initial = t1;
  const animatedValue = useSpring(initial, TRANSITION);
  let t2;
  let t3;
  if ($[2] !== animatedValue || $[3] !== valueRoundedToPlace) {
    t2 = () => {
      animatedValue.set(valueRoundedToPlace);
    };
    t3 = [animatedValue, valueRoundedToPlace];
    $[2] = animatedValue;
    $[3] = valueRoundedToPlace;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="invisible">0</div>;
    t5 = Array.from({
      length: 10
    }, _temp);
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  let t6;
  if ($[8] !== animatedValue) {
    t6 = <div className="relative inline-block w-[1ch] overflow-y-clip overflow-x-visible tabular-nums leading-none">{t4}{t5.map(digit => <SlidingDigit key={digit} mv={animatedValue} number={digit} />)}</div>;
    $[8] = animatedValue;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
function _temp(_, i) {
  return i;
}
function SlidingDigit(t0) {
  const $ = _c(13);
  const {
    mv,
    number
  } = t0;
  const uniqueId = useId();
  const [ref, bounds] = useMeasure();
  let t1;
  if ($[0] !== bounds.height || $[1] !== number) {
    t1 = latest => {
      if (!bounds.height) {
        return 0;
      }
      const placeValue = latest % 10;
      const offset = (10 + number - placeValue) % 10;
      let memo = offset * bounds.height;
      if (offset > 5) {
        memo = memo - 10 * bounds.height;
      }
      return memo;
    };
    $[0] = bounds.height;
    $[1] = number;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const y = useTransform(mv, t1);
  if (!bounds.height) {
    let t2;
    if ($[3] !== number || $[4] !== ref) {
      t2 = <span ref={ref} className="invisible absolute">{number}</span>;
      $[3] = number;
      $[4] = ref;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t2;
  if ($[6] !== y) {
    t2 = {
      y
    };
    $[6] = y;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = `${uniqueId}-${number}`;
  let t4;
  if ($[8] !== number || $[9] !== ref || $[10] !== t2 || $[11] !== t3) {
    t4 = <motion.span style={t2} layoutId={t3} className="absolute inset-0 flex items-center justify-center" transition={TRANSITION} ref={ref}>{number}</motion.span>;
    $[8] = number;
    $[9] = ref;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
type SlidingNumberProps = {
  value: number;
  padStart?: boolean;
  decimalSeparator?: string;
};
export function SlidingNumber(t0) {
  const $ = _c(14);
  const {
    value,
    padStart: t1,
    decimalSeparator: t2
  } = t0;
  const padStart = t1 === undefined ? false : t1;
  const decimalSeparator = t2 === undefined ? "." : t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== decimalSeparator || $[1] !== padStart || $[2] !== value) {
    const absValue = Math.abs(value);
    const [integerPart, decimalPart] = absValue.toString().split(".");
    const integerValue = parseInt(integerPart, 10);
    const paddedInteger = padStart && integerValue < 10 ? `0${integerPart}` : integerPart;
    const integerDigits = paddedInteger.split("");
    const integerPlaces = integerDigits.map((_, i) => 10 ** (integerDigits.length - i - 1));
    const decimalPlaces = decimalPart ? decimalPart.split("").map((__0, i_0) => 10 ** (decimalPart.length - i_0 - 1)) : [];
    t3 = "flex items-center";
    t4 = value < 0 && "-";
    let t7;
    if ($[7] !== integerValue) {
      t7 = place => <Digit key={`pos-${place}`} value={integerValue} place={place} />;
      $[7] = integerValue;
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    t5 = integerPlaces.map(t7);
    t6 = decimalPart && <><span>{decimalSeparator}</span>{decimalPlaces.map(place_0 => <Digit key={`decimal-${place_0}`} value={parseInt(decimalPart, 10)} place={place_0} />)}</>;
    $[0] = decimalSeparator;
    $[1] = padStart;
    $[2] = value;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
  } else {
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[9] !== t3 || $[10] !== t4 || $[11] !== t5 || $[12] !== t6) {
    t7 = <div className={t3}>{t4}{t5}{t6}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}